import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import { INVENTORY_TRANSFER_ITEMS_EDITABLE_STATUSES, type InventoryTransferStatus } from './inventoryTransfer';

/**
 * The lots on an inventory transfer — its items and the pallet tags each one moves.
 *
 *   view ──────────────────────────────────────▶ allow
 *   else ──▶ transfer DRAFT | SUBMITTED ──▶ has a Ship From ──▶ allow
 *                 ✗ ITEMS_LOCKED              ✗ SHIP_FROM_REQUIRED
 *
 * The Ship From matters because the lot picker and the scanner search THAT warehouse.
 */
export type InventoryTransferItemFacts = {
  inventoryTransferStatus: InventoryTransferStatus;
  /** `inventoryTransferHasShipFrom` — the transfer's shipment has an origin. */
  hasShipFrom: boolean;
};

/** The one Ship From test, shared so the web form and the api projection read the same fact. */
export const inventoryTransferHasShipFrom = (originId: number | null | undefined): boolean => originId != null;

export const INVENTORY_TRANSFER_ITEM_FIELDS = ['lot', 'unitsTransferred', 'palletTags'] as const;

export type InventoryTransferItemField = (typeof INVENTORY_TRANSFER_ITEM_FIELDS)[number];

const decideEntity = (facts: InventoryTransferItemFacts, action: Action<InventoryTransferItemField>): Decision => {
  if (action.type === 'view') return allow();

  if (!INVENTORY_TRANSFER_ITEMS_EDITABLE_STATUSES.includes(facts.inventoryTransferStatus)) {
    return deny(
      'INVENTORY_TRANSFER_ITEMS_LOCKED',
      `Lots are locked once the transfer is ${getStatusLabel(facts.inventoryTransferStatus)}.`
    );
  }

  if (!facts.hasShipFrom) {
    return deny('INVENTORY_TRANSFER_ITEMS_SHIP_FROM_REQUIRED', 'Set Ship From to add lots.');
  }

  return allow();
};

export const inventoryTransferItemPolicy: Policy<InventoryTransferItemFacts, InventoryTransferItemField> = definePolicy<
  InventoryTransferItemFacts,
  InventoryTransferItemField
>({
  kind: 'inventory transfer lot',
  fields: INVENTORY_TRANSFER_ITEM_FIELDS,
  ops: [],
  requires: {},
  entity: decideEntity,
});
